import {
    IonContent,
    IonHeader,
    IonIcon,
    IonPage,
    IonList,
    IonItem,
    IonLabel,
    useIonViewWillEnter,
    useIonViewWillLeave,
    isPlatform,
    IonText,
    IonButton,
    IonModal,
    IonToolbar,
    IonTitle,
    IonButtons,
    IonGrid,
    IonCol,
    IonRow,
    IonSegment,
    IonSegmentButton,
    IonInput,
    IonTextarea,
    IonToast,
  } from '@ionic/react';
import { moon, volumeHigh, volumeMute, moonOutline, radio, radioOutline, pause, play, playCircle, pencil, save, close, checkmarkCircleOutline, archiveOutline } from 'ionicons/icons';
import { Swiper, SwiperSlide } from 'swiper/react';
import SwiperCore from "swiper";
import 'swiper/swiper.scss'; // core Swiper
import './PrayNowPage.scss';
import React, { useState, useRef, useContext, useEffect} from 'react';
import PageHeader from '../../components/PageHeader/PageHeader';
import IRouteInfo from '../../interfaces/IRouteInfo';
import IPrayer from '../../interfaces/IPrayer';
import PrayerService from '../../services/PrayerService';
import PrayerStatus, { PrayerStatusNames, PrayerStatusValues } from '../../data/PrayerStatus';
import PrayerSettingsService from '../../services/PrayerSettingsService';
import IPrayerSettings from '../../interfaces/IPrayerSettings';
import NoSleepContext from '../../components/NoSleepContext/NoSleepContext';
import { Media, MediaObject } from '@awesome-cordova-plugins/media';
import { File } from '@awesome-cordova-plugins/file';
import { InsomniaOriginal } from '@awesome-cordova-plugins/insomnia';
import NoSleep from 'nosleep.js';
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import DateHelperService from '../../services/DateHelperService';
import { Keyboard } from '@capacitor/keyboard';

  interface IPrayNowProps extends IRouteInfo {
    hideTabs: Function,
  }
  
  const PrayNow: React.FC<IPrayNowProps> = (props) => {
    const [myActivePrayers, setMyActivePrayers] = useState<IPrayer[]>([]);
    const [activePrayer, setActivePrayer] = useState<IPrayer | undefined>(undefined);
    const [editedPrayer, setEditedPrayer] = useState<IPrayer | undefined>(undefined);
    const [nightModeEnabled, setNightModeEnabled] = useState(false);
    const [transitionSoundEnabled, setTransitionSoundEnabled] = useState(false);
    const [transitionHapticEnabled, setTransitionHapticEnabled] = useState(false);
    const [remainingTime, setRemainingTime] = useState<number>(0);
    const [finishedPrayingTime, setFinishedPrayingTime] = useState<string>('');
    const [prayerSessionPaused, setPrayerSessionPaused] = useState<boolean>(false);
    const [isTimedPrayer, setIsTimedPrayer] = useState<boolean>(false);
    const [showEditPrayerModal, setShowEditPrayerModal] = useState(false);
    const [displayToastSuccess, setDisplayToastSuccess] = useState(false);
    const [displayToastFailed, setDisplayToastFailed] = useState(false);
    const [toastSuccessMessage, setToastSuccessMessage] = useState('Success: Added New Prayer.');
    const [keyboardVisible, setKeyboardVisible] = useState(false);
    const noActivePrayers = {id:0, title:'No Active Prayers', status: PrayerStatus.active, createdDate: new Date(Date.now()), modifiedDate: new Date(Date.now())};
    const completedPrayer = {id:2000000, title:'FINISHED', description:'THANK YOU FOR PRAYING', status: PrayerStatus.active, createdDate: new Date(Date.now()), modifiedDate: new Date(Date.now())};
    let prayerSettings = useRef<IPrayerSettings | undefined>(undefined);
    const slider = useRef<SwiperCore>();
    const endTime = useRef(Date.now());
    const pausedTime = useRef<number>(0);
    const timerId = useRef(0);
    const finishTimerId = useRef(1);
    const pausedTimerId = useRef(2);
    const prayerSessionCompleted = useRef(false);
    const transitionAudio = useRef<HTMLAudioElement | undefined>(undefined);
    const finishedAudio = useRef<HTMLAudioElement | undefined>(undefined);
    const hybridTransitionAudioFile = useRef<MediaObject | undefined>(undefined);
    const hybridFinishedAudioFile = useRef<MediaObject | undefined>(undefined);
    const noSleep = useContext(NoSleepContext);
    const noSleepActive = useRef(false);
    const prayerList = useRef<IPrayer[]>([]);

    // Load Audio Files
    useEffect(() => {
      if (isPlatform('hybrid') && isPlatform('android')) {
        const transitionFilePath = `${File.applicationDirectory}public/assets/audio/transition-piano.mp3`;
        const finishedFilePath = `${File.applicationDirectory}public/assets/audio/finished-piano.mp3`;
        hybridTransitionAudioFile.current = Media.create(isPlatform('ios') ? transitionFilePath.replace('file://', '') : transitionFilePath);
        hybridFinishedAudioFile.current = Media.create(isPlatform('ios') ? finishedFilePath.replace('file://', '') : finishedFilePath);
      } else {
        transitionAudio.current = new Audio('../../assets/audio/transition-piano.mp3');
        finishedAudio.current = new Audio('../../assets/audio/finished-piano.mp3');
      } 

      if(isPlatform('hybrid')) {
        Keyboard.addListener('keyboardDidShow', info => {
          setKeyboardVisible(true);
        });  
        Keyboard.addListener('keyboardDidHide', () => {
          setKeyboardVisible(false);
        });
      } else {
        window.addEventListener('ionKeyboardDidShow', () => {
          setKeyboardVisible(true);
        });
        window.addEventListener('ionKeyboardDidHide', () => {
          setKeyboardVisible(false);
        });
      }
      
      return () => {
          if (isPlatform('hybrid') && isPlatform('android')) {
              (hybridTransitionAudioFile.current as MediaObject)?.release();
              (hybridFinishedAudioFile.current as MediaObject)?.release();
          } else {
              // Not unloading options for HTMLAudioElement
          }
          if(isPlatform('hybrid')) {
            Keyboard.removeAllListeners();
          } else {
            window.removeEventListener('ionKeyboardDidShow', ()=>{});
            window.removeEventListener('ionKeyboardDidHide', ()=>{});
          }
          // In case someone hits the back button or swipes back out of the page: reset and clear the timers
          clearSettings();
      }
  }, []);

    useIonViewWillEnter(async () => {
      props.hideTabs(true);
      prayerSessionCompleted.current = false;
      if(slider.current){
        slider.current.slideTo(0,0);
      }
      prayerSettings.current = await loadPrayerSettings();
      setNightModeEnabled(!!prayerSettings.current?.nightMode);
      setTransitionSoundEnabled(!!prayerSettings.current?.alert);
      setTransitionHapticEnabled(!!prayerSettings.current?.hapticAlert);
      const prayerSessionDuration = prayerSettings.current?.sessionTime * 60 * 1000;
      endTime.current = Date.now() + prayerSessionDuration;

      // Load Prayers
      prayerList.current = await loadPrayers();

      if(!!prayerSettings.current?.shuffle){
        shuffle(prayerList.current);
      }
      if(prayerList.current[0].title !== noActivePrayers.title) {
        prayerList.current.push(completedPrayer);
      }
      setMyActivePrayers(prayerList.current);
      setActivePrayer(prayerList.current[0]);

      if(!!prayerSettings.current?.timed && prayerList.current[0].title !== noActivePrayers.title) {
        setIsTimedPrayer(true);
        noSleepActive.current = true;
        startTimer(
          endTime.current,
          prayerSettings.current?.minimumPrayerTime!,
          prayerList.current.length,
          0);
        // set a timer to stop praying at end time if not done
        startMaxTimer(prayerSessionDuration);
      }
    });

    useIonViewWillLeave(() => {
      clearSettings();
    });

    const clearSettings = () => {
      window.clearInterval(timerId.current);
      window.clearInterval(finishTimerId.current);
      window.clearInterval(pausedTimerId.current);
      props.hideTabs(false);
      if(noSleepActive.current) {
        disableNoSleep();
      }
    }

    const disableNoSleep = () => {
      noSleepActive.current = false;
      if(isPlatform('hybrid')) {
        const awake = noSleep as InsomniaOriginal;
        awake?.allowSleepAgain();
      } else if (!isPlatform('ios')) {
        const awake = noSleep as NoSleep;
        awake?.disable();
      } 
    }
  
    const loadPrayers = async () => {
      let myPrayers: IPrayer[] = [];
      await PrayerService.getPrayersByStatus(PrayerStatus.active).then(result => {
        if(result.Success) {
          if(result.Data?.length){
            myPrayers = result.Data!
          } else {
            myPrayers = [noActivePrayers];
          }
        } else {
          myPrayers = [noActivePrayers];
        }
      });
      return myPrayers;
    };

    const loadPrayerSettings = async () => {
      let initPrayerSettings: IPrayerSettings = {
        id: 0,
        timed: false,
        minimumPrayerTime: 30,
        sessionTime: 10,
        shuffle: false,
        alert: false,
        showSettings: true,
        nightMode: false,
        hapticAlert: true,
      }

      await PrayerSettingsService.getPrayerSettings().then(response => {
        if(response.Success) {
          initPrayerSettings = response.Data!
        }
      });

      return initPrayerSettings;
    };

    const startTimer = (end: number, minimum: number, total:number, position: number) => {
      const activePrayerIndex = slider.current?.activeIndex ? slider.current?.activeIndex : 0;
      const activePrayer = myActivePrayers.length ? myActivePrayers[activePrayerIndex] : prayerList.current[activePrayerIndex]; 
      setActivePrayer(activePrayer);
      const prayersRemaining = total - position - 1;
      const remainingTime = end + pausedTime.current - Date.now();
      const minimumPrayerTime = minimum * 1000;
      const calculatedDuration = remainingTime/prayersRemaining;

      const prayerInterval = calculatedDuration > minimumPrayerTime ? calculatedDuration : minimumPrayerTime;
      window.clearInterval(timerId.current);
      timerId.current = window.setInterval(() => {
        console.log(position, total);
        if(!!prayerSettings.current?.alert && position !== (total - 2)){
          if(isPlatform('hybrid') && isPlatform('android')) {
            hybridTransitionAudioFile.current?.stop();
            hybridTransitionAudioFile.current?.play({numberOfLoops: 1, playAudioWhenScreenIsLocked: true});
          } else {
            transitionAudio.current?.play();
          }         
        }
        if(!!prayerSettings.current?.hapticAlert && position !== (total - 2)){
          if(!isPlatform('desktop')) {
            Haptics.impact({ style: ImpactStyle.Light });
          }
        }
        slider.current?.slideNext();

        if(!prayerSessionCompleted.current){
          setActivePrayer(prayerList.current[slider.current?.activeIndex ?? 1]);
        }
      }, prayerInterval);
    }

    const startMaxTimer = (timeTillDone: number) => {
      if(prayerSettings.current) {

        // set a timer to stop praying at end time if not done
        window.clearInterval(finishTimerId.current);
        finishTimerId.current = window.setInterval(() => {
          if(!prayerSessionCompleted.current){
            slider.current?.slideTo((prayerList.current.length + 1), 1000);
            prayerSessionCompleted.current = true;
          }
          //clear timer when complete
          window.clearInterval(finishTimerId.current);
        }, (timeTillDone));
      }
    }

    const togglePauseTimer = () => {
      if(!prayerSessionPaused) {
        console.log('PAUSED');
        slider.current?.disable();
        window.clearInterval(finishTimerId.current);
        window.clearInterval(timerId.current);
        setRemainingTime(endTime.current + pausedTime.current - Date.now());
        window.clearInterval(pausedTimerId.current);
        pausedTimerId.current = window.setInterval(() => {
          pausedTime.current = pausedTime.current + 1000;
          const timeLeft = endTime.current + pausedTime.current - Date.now();
          setFinishedPrayingTime(new Date(Date.now() + timeLeft).toLocaleTimeString());
        }, 1000);
      } else if(!prayerSessionCompleted.current) {
        console.log('PLAY', remainingTime);
        slider.current?.enable();
        startMaxTimer(remainingTime);
        window.clearInterval(pausedTimerId.current);
        startTimer(endTime.current,
          prayerSettings.current?.minimumPrayerTime!,
          myActivePrayers.length,
          slider.current?.activeIndex ?? 1);
      }
      setPrayerSessionPaused(!prayerSessionPaused);
    }

    // On manual advance recalculate and reset calculated advance
    const timedPrayerAdvance = () => {
      if(!!prayerSettings.current?.timed && !prayerSessionCompleted.current){
        startTimer(endTime.current,
          prayerSettings.current?.minimumPrayerTime!,
          myActivePrayers.length,
          slider.current?.activeIndex ?? 1);
      } else {
        setActivePrayer(myActivePrayers[slider.current?.activeIndex ?? 1]);
      }
    }

    const toggleNightMode = () => {
      setNightModeEnabled(!nightModeEnabled);
      const newSettings = {...prayerSettings.current!, nightMode:!nightModeEnabled};
      PrayerSettingsService.updatePrayerSettings(newSettings).then((result) =>
        prayerSettings.current = result.Success ? result.Data : prayerSettings.current
      );
    }

    const toggleTransitionSound = () => {
      if(!!prayerSettings.current?.timed){
        setTransitionSoundEnabled(!transitionSoundEnabled);
        const newSettings = {...prayerSettings.current!, alert:!transitionSoundEnabled};
        PrayerSettingsService.updatePrayerSettings(newSettings).then((result) =>
          prayerSettings.current = result.Success ? result.Data : prayerSettings.current
        );
      }
    }

    const toggleTransitionHaptic = () => {
      if(!!prayerSettings.current?.timed){
        setTransitionHapticEnabled(!transitionHapticEnabled);
        const newSettings = {...prayerSettings.current!, hapticAlert:!transitionHapticEnabled};
        PrayerSettingsService.updatePrayerSettings(newSettings).then((result) =>
          prayerSettings.current = result.Success ? result.Data : prayerSettings.current
        );
      }
    }

    const shuffle = (array: Array<any>) => {
      for (let i = array.length - 1; i > 0; i--) {
        let j = Math.floor(Math.random() * (i + 1));
        [array[i], array[j]] = [array[j], array[i]];
      }
    }

    const editCurrentPrayer = () => {
      if(slider.current?.activeIndex !== myActivePrayers.length - 1) {
        setEditedPrayer(activePrayer);
        setShowEditPrayerModal(true);
        if(!prayerSessionPaused && !prayerSessionCompleted.current && isTimedPrayer) {
          togglePauseTimer();
        }
      }
    }

    const saveEditedPrayer = async () => {
      const today = new Date(Date.now());  
      const currentPrayer: IPrayer = { ...editedPrayer!,
        modifiedDate: today,
      };

      await PrayerService.updatePrayer(currentPrayer).then(result => {
        if(result.Success) {
          setToastSuccessMessage(result.Message.UserMessage)
          setDisplayToastSuccess(true);
          setActivePrayer(editedPrayer);
          const editedPrayers = myActivePrayers.map(prayer => {
            if(prayer.id === editedPrayer?.id) {
              return editedPrayer;
            } else {
              return prayer;
            }
          });
          setMyActivePrayers(editedPrayers);
          setShowEditPrayerModal(false);
        } else {
          setDisplayToastFailed(true);
        }
      });
    }  

    const pageHeaderMenuContent = 
      <IonList className="prayer-page-menu-list">
        <IonItem onClick={editCurrentPrayer}>
          <IonLabel>EDIT PRAYER</IonLabel>
          <IonIcon slot="end" icon={pencil}></IonIcon>
        </IonItem>
        <IonItem onClick={toggleNightMode}>
          <IonLabel>NIGHT MODE</IonLabel>
          <IonIcon slot="end" icon={nightModeEnabled ? moon : moonOutline}></IonIcon>
        </IonItem>
        { isTimedPrayer && 
        <IonItem onClick={togglePauseTimer}>
          <IonLabel>{prayerSessionPaused ? 'CONTINUE PRAYER' : 'PAUSE PRAYER'}</IonLabel>
          <IonIcon slot="end" icon={prayerSessionPaused ? play : pause}></IonIcon>
        </IonItem>
        }
        { isTimedPrayer && 
        <IonItem onClick={toggleTransitionSound}>
          <IonLabel>ALERT SOUND</IonLabel>
          <IonIcon slot="end" icon={transitionSoundEnabled && !!prayerSettings.current?.timed ? volumeHigh : volumeMute}></IonIcon>
        </IonItem>
        }
        { (!(isPlatform('desktop') && isPlatform('tablet') && !isPlatform('hybrid')) && !(isPlatform('ios') && isPlatform('mobileweb'))) &&
          <IonItem onClick={toggleTransitionHaptic}>
            <IonLabel>ALERT VIBRATION</IonLabel>
            <IonIcon slot="end" icon={transitionHapticEnabled && !!prayerSettings.current?.timed ? radio : radioOutline}></IonIcon>
          </IonItem>
        }
      </IonList>;

    return (
      <IonPage className="pray-now-page">
        <IonHeader>
        <PageHeader {...props} showBackButton={true} menuContents={pageHeaderMenuContent}></PageHeader>
        </IonHeader>
        <IonContent color="prayer-shade">
        <Swiper onInit={(core) => slider.current = core} className={`prayer-slides ${nightModeEnabled ? 'night-mode' : 'day-mode'}`} 
          centeredSlides={true} preventClicks={false} preventClicksPropagation={false} onSlideChange={timedPrayerAdvance}
          onReachEnd={() => {
              if(myActivePrayers.length && !!prayerSettings.current?.timed) {
                window.clearInterval(timerId.current);
                setIsTimedPrayer(false);
                prayerSessionCompleted.current = true;
                if(!!prayerSettings.current.alert) {
                  if(isPlatform('hybrid') && isPlatform('android')) {
                    hybridFinishedAudioFile.current?.play({numberOfLoops: 1, playAudioWhenScreenIsLocked: true});
                  } else {
                    finishedAudio.current?.play();
                  }   
                }
                if(!!prayerSettings.current?.hapticAlert){
                  if(!isPlatform('desktop')) {
                    Haptics.impact({ style: ImpactStyle.Light });
                  }
                }
                if(noSleepActive.current) {
                  disableNoSleep();
                }
              }}}
        >
            {
              myActivePrayers.map(prayer =>
                <SwiperSlide key={prayer.id}>
                  <div className="prayer-container">
                    <div>
                      { prayer.status === PrayerStatus.answered && 
                        <div className='prayer-status' onClick={()=>editCurrentPrayer()}>
                          <IonIcon icon={checkmarkCircleOutline}></IonIcon> ANSWERED</div>
                      }
                      { prayer.status === PrayerStatus.revisit && 
                        <div className='prayer-status' onClick={()=>editCurrentPrayer()}>
                          <IonIcon icon={archiveOutline}></IonIcon> REVISIT</div>
                      }
                      <h1 onClick={()=>editCurrentPrayer()}>{prayer.title}</h1>
                      <p>{prayer.description?.split('\n').map((item, key) => {
                            return <span key={key}>{item}<br/></span>
                          })}
                      </p>
                    </div>
                  </div>
                </SwiperSlide>
              )
            }
        </Swiper>
        { prayerSessionPaused &&
            <div className='prayer-paused-wrapper'>
              <div className='prayer-paused-container' onClick={togglePauseTimer}>
                <h3>PRAY TIMER PAUSED</h3>
                <IonIcon slot="icon-only" icon={playCircle} color="secondary" />
                <h2>CONTINUE PRAYING</h2>
                <IonText>Time Remaining <b>{DateHelperService.formatDuration(remainingTime)}</b></IonText><br />
                <IonText>Finished Praying Time <b>{finishedPrayingTime}</b></IonText>
              </div>
            </div>
          }
          <IonModal
            isOpen={showEditPrayerModal}
            onDidDismiss={() => setShowEditPrayerModal(false)}
            breakpoints={isPlatform("mobile") ? undefined : [0, 0.75, 1]}
            initialBreakpoint={isPlatform("mobile") ? undefined : 1}
            className={`edit-prayer-modal ${nightModeEnabled ? 'prayer-modal-night-mode' : 'prayer-modal-day-mode'}`}
            mode='md'
            canDismiss={true}>
            <IonContent className={`edit-prayer-modal-content 
              ${nightModeEnabled ? 'prayer-modal-night-mode' : 'prayer-modal-day-mode'}`}
              >
              <IonHeader>
                <IonToolbar>
                  <IonTitle className={isPlatform("mobile") ? 'prayer-title-mobile' : ''}>EDIT PRAYER</IonTitle>
                  <IonButtons slot="end">
                    <IonButton className="save-button" fill="outline" shape="round" onClick={() => saveEditedPrayer()} >
                      <IonIcon slot="start" icon={save} />
                      <IonLabel>SAVE</IonLabel>
                    </IonButton>
                    <IonButton onClick={() => setShowEditPrayerModal(false)} >
                      <IonIcon slot="icon-only" icon={close}/>
                    </IonButton>
                  </IonButtons>
                </IonToolbar>
              </IonHeader>
              <IonGrid>
                <IonRow>
                  <IonCol className="edit-prayer-modal-status">
                    <IonSegment mode="md" value={editedPrayer?.status}
                      onIonChange={e => setEditedPrayer({...editedPrayer!, status: e.detail.value?.toString() as PrayerStatusValues})}
                      className="pray-status-selector">
                      <IonSegmentButton value={PrayerStatus.revisit}>
                        <IonLabel>{PrayerStatusNames[PrayerStatus.revisit]}</IonLabel>
                      </IonSegmentButton>
                      <IonSegmentButton value={PrayerStatus.active}>
                        <IonLabel>{PrayerStatusNames[PrayerStatus.active]}</IonLabel>
                      </IonSegmentButton>
                      <IonSegmentButton value={PrayerStatus.answered}>
                        <IonLabel>{PrayerStatusNames[PrayerStatus.answered]}</IonLabel>
                      </IonSegmentButton> 
                    </IonSegment>
                  </IonCol>
                </IonRow>
                <IonRow className="edit-prayer-modal-title">
                  <IonCol>
                    <IonItem>
                      <IonLabel position="stacked" color="secondary" className="edit-prayer-modal-label">TITLE:</IonLabel>
                      <IonInput aria-label="TITLE" value={editedPrayer?.title} tabIndex={0} className="edit-prayer-modal-input"
                        onInput={e => setEditedPrayer({...editedPrayer!, title: e.currentTarget.value?.toString() ?? ''})}>
                      </IonInput>
                    </IonItem>
                  </IonCol>
                </IonRow>
                <IonRow>
                  <IonCol>
                    <IonItem lines="none" className="">
                      <IonLabel position="stacked" color="secondary" className="edit-prayer-modal-label">DESCRIPTION:</IonLabel>
                      <IonTextarea aria-label="DESCRIPTION" value={editedPrayer?.description} autoGrow={true} autoCapitalize='sentences'
                        onInput={e => setEditedPrayer({...editedPrayer!, description: e.currentTarget.value?.toString() ?? ''})}
                        className={isPlatform("mobile") && keyboardVisible && false ? 'prayer-edit-description-mobile edit-prayer-modal-input' : 'edit-prayer-modal-input'}
                        >
                      </IonTextarea>
                    </IonItem>
                  </IonCol>
                </IonRow>
              </IonGrid>
            </IonContent>
          </IonModal>
          <IonToast color="success"
            isOpen={displayToastSuccess}
            onDidDismiss={() => setDisplayToastSuccess(false)}
            message={toastSuccessMessage}
            duration={2500} />
          <IonToast color="danger"
            isOpen={displayToastFailed}
            onDidDismiss={() => setDisplayToastFailed(false)}
            message="Error: Failed to Update Prayer List."
            duration={2500} />
        </IonContent>
      </IonPage>
    );
  };
  
  export default PrayNow;
  